
// Pure geometric core for the "Rektangel" tool - an exact, matematisk
// rectangle (4 corners, 90° neighbour angles, parallel opposite sides) from
// three clicked 3D points. No DOM/THREE dependency - plain {x,y,z} points,
// Level 1 (no-browser) unit testable - see rectangleFromThreePoints.test.js.
//
//   Rektangel (geometry core)
//           |
//           +-- fitRectangleFromThreePoints() - anchor/basis/length/width from 3 clicks
//           +-- buildRectangleCorners()        - 4 corners from a {anchor,u,v,lengthM,widthM} definition
//
// ── The 3-click method and its stable reference ─────────────────────────
// Click 1 (`p1`) is the rectangle's ANCHOR corner. Click 2 (`p2`) fixes the
// base DIRECTION `u = normalize(p2-p1)` and the initial length
// `|p2-p1|`. Click 3 (`p3`) fixes the WIDTH direction `v` - the component of
// `(p3-p1)` perpendicular to `u` (Gram-Schmidt), normalised - and the
// initial width `|that perpendicular component|`. `{anchor: p1, u, v}`
// together ARE the rectangle's local 3D plane and orientation, and - unless
// a later revision finds a more intuitive rule from analysing the editing
// lifecycle further - are the STABLE reference kept fixed for the whole
// life of the shape: every live length/width edit (and later "Rediger
// rektangel…") regenerates the 4 corners from this same anchor+u+v, never
// re-derived from the CURRENT corners (which could have drifted under
// floating-point noise across repeated edits). `u`/`v` are always exactly
// orthogonal by construction (Gram-Schmidt), so the result is always a true
// rectangle, never a general parallelogram.
//
// ── Degenerate input ─────────────────────────────────────────────────────
// Rejected (returns null) when p1≈p2 (no base direction at all) or when p3
// is too close to the LINE through p1/p2 (no usable width direction -
// scale-invariant: measured as the perpendicular offset relative to the
// base length, so it rejects consistently regardless of capture scale).

function sub(a, b){ return {x: a.x - b.x, y: a.y - b.y, z: a.z - b.z}; }
function add(a, b){ return {x: a.x + b.x, y: a.y + b.y, z: a.z + b.z}; }
function dot3(a, b){ return a.x * b.x + a.y * b.y + a.z * b.z; }
function len3(a){ return Math.hypot(a.x, a.y, a.z); }
function scale3(a, s){ return {x: a.x * s, y: a.y * s, z: a.z * s}; }
function normalize3(a){
	const l = len3(a);
	return l > 1e-12 ? scale3(a, 1 / l) : {x: 0, y: 0, z: 0};
}

const DEGENERATE_RATIO_EPS = 1e-4; // |perpendicular component| / |base length| below this counts as collinear

// Returns {anchor, u, v, lengthM, widthM} (u, v unit, mutually orthogonal;
// widthM always >= 0 - `v` is defined to point toward p3, so the initial
// rectangle always includes the third click by construction) or null for
// degenerate input.
export function fitRectangleFromThreePoints(p1, p2, p3){
	const base = sub(p2, p1);
	const baseLen = len3(base);
	if(baseLen < 1e-9) return null; // p1 ≈ p2 - no base direction

	const u = normalize3(base);
	const e2 = sub(p3, p1);
	const along = dot3(e2, u);
	const perp = {x: e2.x - along * u.x, y: e2.y - along * u.y, z: e2.z - along * u.z};
	const perpLen = len3(perp);

	if(perpLen / baseLen < DEGENERATE_RATIO_EPS) return null; // p3 too close to the p1-p2 line

	const v = normalize3(perp);
	return {anchor: {x: p1.x, y: p1.y, z: p1.z}, u, v, lengthM: baseLen, widthM: perpLen};
}

// Regenerates the 4 corners from a {anchor,u,v,lengthM,widthM} definition -
// `widthM` may be signed ("Vend side" - task DEL C - is just widthM -> -widthM,
// flipping which side of the base line the rectangle occupies; anchor/u/v
// never change). Corner order c0..c3 walks the rectangle's own perimeter
// (c0=anchor, c1=anchor+L·u, c2=anchor+L·u+W·v, c3=anchor+W·v) - a closed
// ring, same "FeatureVisual closed-polygon" convention every other Polygon
// feature in this codebase uses (no repeated closing vertex).
export function buildRectangleCorners(anchor, u, v, lengthM, widthM){
	const c0 = {x: anchor.x, y: anchor.y, z: anchor.z};
	const c1 = add(c0, scale3(u, lengthM));
	const c3 = add(c0, scale3(v, widthM));
	const c2 = add(c1, scale3(v, widthM));
	return [c0, c1, c2, c3];
}
